import { CommonModule, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveComponentModule } from '@ngrx/component';
import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule, select } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { distinctUntilChanged, filter, first, take } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { ContainerComponent } from './components/container/container.component';
import { HeaderButtonComponent } from './components/container/header-button/header-button.component';
import { ProfileButtonComponent } from './components/container/profile-button/profile-button.component';
import { SpinnerComponent } from './components/container/spinner/spinner.component';
import { EngineGatePageComponent } from './components/container/engine-gate-page/engine-gate-page.component';
import { MonitoringNavComponent } from './components/monitoring-nav/monitoring-nav.component';
import { MaterialModule } from './modules/material.module';
import { OnboardingService } from './modules/msp/services/onboarding.service';
import { loadMsp } from './modules/msp/store/msp/actions';
import { Msp } from './modules/msp/store/msp/model';
import { SnackBarComponent } from './modules/shared/components/snack-bar/snack-bar.component';
import { LoaderModule } from './modules/shared/libs/loader/loader.module';
import { SharedModule } from './modules/shared/shared.module';
import { SwayTemplateItemsEffects } from './modules/sway/store/templates/items/effects';
import { SwayTemplatesEffects } from './modules/sway/store/templates/templates/effects';
import { PermissionDialogComponent } from './services/guards/permission-dialog/permission-dialog.component';
import { InitClientService } from './services/init-client.service';
import { TokenInterceptor } from './services/interceptors/token.interceptor';
import { WebsocketService } from './services/websocket.service';
// SUB CLIENT STORE EFFECTS
import { AlertEffects as _AlertEffects } from './stores/client/alert/effects';
import { EventEffects as _EventEffects } from './stores/client/etl/event/effects';
import { SummaryEffects } from './stores/client/etl/summary/effects';
import { AuthenticationStrengthPoliciesEffect } from './stores/client/graph/authentication-strength-policies/effects';
import { AuthenticationMethodsEffects } from './stores/client/graph/authentication/methods/effects';
import { PerUserMfaStateEffects } from './stores/client/graph/authentication/perUserMfaState/effects';
import { GraphAuthorizationPolicyEffects } from './stores/client/graph/authorization-policy/effects';
import { GraphDirectorySettingsEffects } from './stores/client/graph/directory-settings/effects';
import { GraphCrossTenantAccessPolicyEffects } from './stores/client/graph/cross-tenant-access-policy/effects';
import { CompliancePolicyEffect } from './stores/client/graph/intune/compliance-policies/policies/effects';
import { ConditionalAccessPolicyEffects } from './stores/client/graph/conditional-access/effects';
import { DirectoryRoleEffects } from './stores/client/graph/directory-roles/effects';
import { DirectoryRoleMemberEffects } from './stores/client/graph/directory-roles/members/effects';
import { GraphDomainsEffects } from './stores/client/graph/domains/effects';
import { GroupEffects as _GroupEffects } from './stores/client/graph/group/effects';
import { GroupMembersEffects } from './stores/client/graph/group/members/effects';
import { GraphInboxRulesEffects } from './stores/client/graph/inbox-rules/effect';
import { UserMailboxSettingsEffects } from './stores/client/graph/mailbox-settings/effects';
import { NamedLocationsEffect } from './stores/client/graph/named-locations/effects';
import { SecurityDefaultsEnforcementPolicyEffects } from './stores/client/graph/security-defaults/effects';
import { GraphServicePrincipalAppRolesEffects } from './stores/client/graph/service-principal/app-roles/effects';
import { GraphServicePrincipalEffects } from './stores/client/graph/service-principal/effects';
import { GraphServicePrincipalOAuth2PermissionGrantsEffects } from './stores/client/graph/service-principal/oauth2-permission-grants/effects';
import { GraphSPOSharePointSiteDetailsEffects } from './stores/client/graph/spo/sharepoint-site/effects';
import { GraphSubscribedSkusEffects } from './stores/client/graph/subscribed-skus/effects';
import { GraphUserEffects } from './stores/client/graph/user/user.effects';
import { DismissEffects } from './stores/client/octiga/dismiss/effects';
import { PwnedUserEffects } from './stores/client/octiga/pwned-user/effects';
import { RolesStatusEffects } from './stores/client/octiga/status/roles/effects';
import { SubscriptionsStatusEffects } from './stores/client/octiga/status/subscriptions/effects';
import { TenantEffects as _OctigaTenantEffects } from './stores/client/octiga/tenant/effects';
import { ExoAdminAuditLogConfigEffects as _ExoAdminAuditLogConfigEffects } from './stores/client/powershell/exo/admin-audit-log-config/effects';
import { AntiPhishPolicyEffects } from './stores/client/powershell/exo/anti-phish-policy/effects';
import { SafeAttachmentPolicyEffects } from './stores/client/powershell/exo/safe-attachment-policy/effects';
import { CasMailboxEffects as _CasMailboxEffects } from './stores/client/powershell/exo/cas-mailbox/effects';
import { DKIMSigningConfigEffects } from './stores/client/powershell/exo/dkim-signing-config/effects';
import { EOPProtectionPolicyEffects } from './stores/client/powershell/exo/eop-protection-policy-rule/effects';
import { ExoHostedContentFilterPolicyEffects } from './stores/client/powershell/exo/hosted-content-filter-policy/effects';
import { ExoHostedOutboundSpamFilterPolicyEffects } from './stores/client/powershell/exo/hosted-outbound-spam-filter-policy/effects';
import { MailboxDelegateEffects as _ExoMailboxDelegateEffects } from './stores/client/powershell/exo/mailbox-delegate/effects';
import { MailboxEffects as _MailboxEffects } from './stores/client/powershell/exo/mailbox/effects';
import { MalwareFilterPolicyEffects } from './stores/client/powershell/exo/malware-filter-policy/effects';
import { ExoMobileDeviceMailboxPolicyEffects } from './stores/client/powershell/exo/mobile-device-mailbox-policy/effects';
import { ExoOrganizationConfigEffects as _ExoOrganizationConfigEffects } from './stores/client/powershell/exo/organization-config/effects';
import { ExoQuarantinePolicyEffects } from './stores/client/powershell/exo/quarantine-policy/effects';
import { ExoRemoteDomainEffects as _ExoRemoteDomainEffects } from './stores/client/powershell/exo/remote-domain/effects';
import {
    SharingPolicyEffects,
    SharingPolicyEffects as _SharingPolicyEffects,
} from './stores/client/powershell/exo/sharing-policy/effects';
import { ExoTransportConfigEffects as _ExoTransportConfigEffects } from './stores/client/powershell/exo/transport-config/effects';
import { TransportRuleEffects } from './stores/client/powershell/exo/transport-rule/effects';
import { SpoTenantEffects as _SpoTenantEffects } from './stores/client/powershell/spo/tenant/effects';
import { ReportBaselineEffects as _ReportBaselineEffects } from './stores/client/report/baseline/effects';
import { ReportCurrentPostureCountEffects as _ReportCurrentPostureCountEffects } from './stores/client/report/current_posture_count/effects';
import { ReportDeviationEffects as _ReportDeviationEffects } from './stores/client/report/deviations/effects';
import { ReportScheduleEffects } from './stores/client/report/schedule/effect';
import { ReportPSATenantTicketCountEffects as _ReportPSATenantTicketCountEffects } from './stores/client/report/tickets/effects';
import { SwayBaselineEffects } from './stores/client/sway/baseline/effects';
import { DeviationEffects } from './stores/client/sway/deviation/deviation.effects';
import { GroupEffects } from './stores/client/sway/group/effects';
import { TenantEffects } from './stores/client/sway/tenant/effects';
import { GeoJsonEffects } from './stores/root/google/geojson/effects';
import { DirectoryRoleTemplatesEffects } from './stores/root/graph/directoryRoleTemplates/effects';
import { AsnListEffects } from './stores/root/octiga/asn-list/effects';
import { M365RangesEffects } from './stores/root/octiga/m365-ranges/effects';
import { loadSession } from './stores/root/octiga/session/actions';
import { SessionEffects } from './stores/root/octiga/session/effects';
import { ReportCategoryEffects } from './stores/root/report/category/effects';
import { SwayCategoryEffects } from './stores/root/sway/category/effects';
import { SwayMspConfigEffects } from './stores/root/sway/onboardingStatus/effects';
import { SpecEffects } from './stores/root/sway/spec/effects';
import { AdminConsentRequestPolicyEffects } from './stores/client/graph/admin-consent-request-policy/effects';
import { DeviceConfigurationPoliciesEffects } from './stores/client/graph/intune/device-configuration-policies/effects';
import { MspModuleEffects } from './modules/msp/store/effects';
import { AuthenticationMethodsPoliciesEffects } from './stores/client/graph/authentication-methods-policies/effects';
import { Iso3166_2CountryCodesEffects } from './stores/root/octiga/country-codes/effects';
import { UserAutoBlockEffects } from './stores/client/octiga/user-auto-block/effects';
import { SafeLinksPolicyEffects } from './stores/client/powershell/exo/safe-links-policy/effects';
import * as fromRoot from 'projects/angular-clarity/src/app/stores/root.store';
import { ChangesEffects } from 'projects/angular-clarity/src/app/stores/client/octiga/changes/effects';
import * as MSPFeatureReducers from 'projects/angular-clarity/src/app/modules/msp/store';

@NgModule({
    declarations: [
        ContainerComponent,
        HeaderButtonComponent,
        MonitoringNavComponent,
        ProfileButtonComponent,
        SpinnerComponent,
        EngineGatePageComponent,
        SnackBarComponent,
        PermissionDialogComponent,
    ],
    imports: [
        StoreModule.forRoot(fromRoot.reducers, {
            metaReducers: fromRoot.metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: true,
                strictActionSerializability: false,
            },
        }),

        // StoreModule.forFeature('client', fromClient.reducers),
        EffectsModule.forRoot([SessionEffects]),

        EffectsModule.forFeature([
            TenantEffects,
            SwayBaselineEffects,
            AsnListEffects,
            M365RangesEffects,
            UserAutoBlockEffects,
            GeoJsonEffects,
            AuthenticationMethodsEffects,
            AdminConsentRequestPolicyEffects,
            ReportScheduleEffects,
            ReportCategoryEffects,
            SwayCategoryEffects,
            SpecEffects,
            DeviationEffects,
            GroupEffects,
            ChangesEffects,
            DismissEffects,
            SwayMspConfigEffects,
            DirectoryRoleEffects,
            DeviceConfigurationPoliciesEffects,
            DirectoryRoleTemplatesEffects,
            AuthenticationMethodsPoliciesEffects,
            DirectoryRoleMemberEffects,
            UserMailboxSettingsEffects,
            ExoMobileDeviceMailboxPolicyEffects,
            GraphDomainsEffects,
            ConditionalAccessPolicyEffects,
            GraphAuthorizationPolicyEffects,
            GraphDirectorySettingsEffects,
            GraphCrossTenantAccessPolicyEffects,
            AuthenticationStrengthPoliciesEffect,
            NamedLocationsEffect,
            DKIMSigningConfigEffects,
            SecurityDefaultsEnforcementPolicyEffects,
            GraphSubscribedSkusEffects,
            ExoHostedContentFilterPolicyEffects,
            ExoHostedOutboundSpamFilterPolicyEffects,
            MalwareFilterPolicyEffects,
            SwayTemplateItemsEffects,
            SessionEffects,
            SharingPolicyEffects,
            SummaryEffects,
            AntiPhishPolicyEffects,
            SafeAttachmentPolicyEffects,
            SafeLinksPolicyEffects,
            ExoQuarantinePolicyEffects,
            EOPProtectionPolicyEffects,
            TransportRuleEffects,
            PerUserMfaStateEffects,
            PwnedUserEffects,
            GroupMembersEffects,
            CompliancePolicyEffect,
            RolesStatusEffects,
            SubscriptionsStatusEffects,
            GraphInboxRulesEffects,
            // SUB CLIENT STORE EFFECTS
            _OctigaTenantEffects,
            _AlertEffects,
            _EventEffects,
            GraphUserEffects,
            _GroupEffects,
            GraphServicePrincipalEffects,
            GraphServicePrincipalAppRolesEffects,
            GraphServicePrincipalOAuth2PermissionGrantsEffects,
            _CasMailboxEffects,
            _MailboxEffects,
            _SharingPolicyEffects,
            _ExoMailboxDelegateEffects,
            _ExoTransportConfigEffects,
            _ExoRemoteDomainEffects,
            _ExoOrganizationConfigEffects,
            _ExoAdminAuditLogConfigEffects,
            _SpoTenantEffects,
            _ReportBaselineEffects,
            _ReportDeviationEffects,
            _ReportCurrentPostureCountEffects,
            _ReportPSATenantTicketCountEffects,
            Iso3166_2CountryCodesEffects,
            // MSP
            ...MspModuleEffects,
            // MSP Templates
            SwayTemplatesEffects,
            // GRAPH
            GraphSPOSharePointSiteDetailsEffects,
        ]),
        StoreModule.forFeature(MSPFeatureReducers.featureKey, MSPFeatureReducers.reducers),
        BrowserModule,
        CommonModule,
        MaterialModule,
        FlexLayoutModule,
        HttpClientModule,
        BrowserAnimationsModule,
        MatSelectModule,
        ReactiveFormsModule,
        ReactiveComponentModule,
        SharedModule,
        MatIconModule,
        AppRoutingModule,
        LoaderModule,
        StoreDevtoolsModule.instrument({
            maxAge: 50,
        }),
    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        DatePipe,
        OnboardingService,
        { provide: Window, useValue: window },
    ],
    bootstrap: [ContainerComponent],
})
export class AppModule {
    iconList = [
        'no-ual',
        'integrations',
        'view-details',
        'owa-forward',
        'inbox-forward',
        'move-item',
        'delete-item',
        'white-labelling',
        'ip',
        'tenant',
        'database',
        'spinner',
        'wave-pulse',
        'light-wave-pulse',
        'box',
        'key',
        'dashboard',
        'globe',
        'lock',
        'warning',
        'danger',
        'critical',
    ];
    constructor(
        private store: Store<any>,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private initService: InitClientService,
        private onboard: OnboardingService,
        private webSocketService: WebsocketService,
    ) {
        this.iconsRegister(this.matIconRegistry, this.domSanitizer);
        this.init();
    }

    init(): void {
        console.log('init');

        this.store.dispatch(loadSession());

        // one time init
        this.store
            .pipe(
                select(fromRoot.selectSession),
                first((state) => state.isAuthenticated),
            )
            .subscribe(({ session }) => {
                for (const tid of Object.keys(session?.tenantAccess ?? {})) {
                    this.initService.addClientReducer(tid);
                }
            });

        // every time client switches init
        this.store
            .pipe(
                select(fromRoot.selectSession),
                filter((state) => state.isAuthenticated && !!state.session.clientId),
                distinctUntilChanged((a, b) => a.session.clientId === b.session.clientId),
            )
            .subscribe((state) => {
                this.initService.loadClient(state.session.clientId);
            });

        // loadMsp
        this.store
            .pipe(
                select(fromRoot.selectSession),
                filter((state) => state.isAuthenticated && !!state.session.msp_id),
                take(1),
            )
            .subscribe((msp) => {
                this.store.dispatch(loadMsp({ msp_id: msp.session.msp_id, userId: msp.session.user_id }));
                this.onboard.init(msp.session.user_id);
            });
    }

    mspIsActive(msp: Msp): boolean {
        return msp.status === 'TRIAL' || msp.status === 'SUBSCRIPTION';
    }

    iconsRegister(iconRegistry: MatIconRegistry, domSanitizer: DomSanitizer): void {
        this.iconList.forEach((iconName) => {
            iconRegistry.addSvgIcon(
                iconName,
                domSanitizer.bypassSecurityTrustResourceUrl('assets/img/icons/' + iconName + '.svg'),
            );
        });
    }
}
